import useSwapHistoryTableData from 'src/components/Swap/hooks/useSwapHistoryTableData';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { UISerializableSwapRecord } from '@drift/common';
import { ArrowRight, Swap } from '@drift-labs/icons';
import { getColorVar } from 'src/utils/theme/colorVars';
import TableV2 from 'src/components/Tables/TableV2';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import useExpandableItem, {
	ExpandableItemParams,
} from 'src/hooks/lists/expandable/useExpandableItem';

interface Props extends ExpandableItemParams {
	swap: UISerializableSwapRecord;
	displayDatesAsTimestamp: boolean;
	isSellDirection: boolean;
	toggleDateDisplayType: () => void;
	toggleSellDirection: () => void;
}

export default function SwapHistoryTableMobileCard({
	swap,
	displayDatesAsTimestamp,
	isSellDirection,
	toggleDateDisplayType,
	toggleSellDirection,
	areAllItemsExpanded,
	handleItemExpandedChange,
}: Props) {
	const { isExpanded, handleExpandedChange } = useExpandableItem({
		areAllItemsExpanded,
		handleItemExpandedChange,
	});

	const {
		fromMarketSymbol,
		toMarketSymbol,
		amountIn,
		amountOut,
		swapPriceText,
		dateFromTimestamp,
		scannerUrl,
	} = useSwapHistoryTableData({ swap, isSellDirection });

	return (
		<MobileTableCard.Root
			expanded={isExpanded}
			setExpanded={handleExpandedChange}
		>
			<MobileTableCard.CollapsibleHeader expanded={isExpanded}>
				<div className="mr-auto flex items-center gap-x-xs typo-t2 text-text-default">
					<MarketIcon
						marketSymbol={fromMarketSymbol ?? ''}
						className="size-5"
					/>
					{amountIn.prettyPrint(true)}
					<ArrowRight color={getColorVar('text-secondary')} />
					<MarketIcon marketSymbol={toMarketSymbol ?? ''} className="size-5" />
					{amountOut.prettyPrint(true)}
				</div>
			</MobileTableCard.CollapsibleHeader>
			<MobileTableCard.CollapsibleContent>
				<div className="flex flex-col gap-y-lg">
					<div className="flex items-center justify-between">
						<MobileTableCard.Label>
							Time
							<IconButton
								size="sm"
								variant="tertiary"
								className="-m-sm p-sm"
								icon={Swap}
								onClick={toggleDateDisplayType}
							/>
						</MobileTableCard.Label>
						<span className="flex items-center gap-x-xs typo-t4">
							<TableV2.DateCell
								date={dateFromTimestamp}
								displayAsTimestamp={displayDatesAsTimestamp}
								className="py-0 whitespace-nowrap"
							/>
						</span>
					</div>
					<div className="flex items-center justify-between">
						<MobileTableCard.Label>Swap Price</MobileTableCard.Label>
						<span className="flex items-center gap-x-xs typo-t4">
							{swapPriceText}
							<IconButton
								size="sm"
								variant="tertiary"
								className="-m-sm p-sm"
								icon={Swap}
								onClick={toggleSellDirection}
							/>
						</span>
					</div>
					<Button
						size="sm"
						variant="secondary"
						as="a"
						href={scannerUrl}
						openInNewTab
					>
						View in scanner
					</Button>
				</div>
			</MobileTableCard.CollapsibleContent>
		</MobileTableCard.Root>
	);
}
